import { OrganizerType } from '@thro-types/events';

import { addDays, differenceInMinutes, isSameDay, set } from 'date-fns';
import { omit, pick } from 'lodash';
import * as yup from 'yup';

import { translations } from 'locales/i18n';
import { Article } from 'types/AppTypes';

export const articleValidationSchema = t => ({
  postAs: yup.string().required(t(translations.validation.required())),
  recipients: yup.array().of(yup.string()).min(1).required(t(translations.validation.required())),
  department: yup.string(),
  isPushNotificationEnabled: yup.boolean(),
  isSendMailDisabled: yup.boolean(),
  semesters: yup
    .array()
    .test('needsSemester', 'recipient "student" needs semester selection', (value, context) => {
      if (
        context.parent &&
        context.parent?.recipients &&
        context.parent?.recipients.filter(recipient => recipient.includes('Studenten')).length !==
          0 &&
        value?.length === 0
      ) {
        return false;
      } else {
        return true;
      }
    }),
  headline: yup.string().required(t(translations.validation.required())),
  shortDescription: yup.string().required(t(translations.validation.required())),
  type: yup
    .string()
    .when('isSuperUser', {
      is: true,
      then: yup.string().oneOf(['event', 'general', 'study', 'legal', 'holiday']),
      otherwise: yup.string().oneOf(['event', 'general', 'study'])
    })
    .required(t(translations.validation.required())),
  articleText: yup.string().required(t(translations.validation.required())),
  withAppointment: yup.boolean(),
  appointment: yup
    .object()
    .when(['type', 'withAppointment'], {
      is: (type, withAppointment) => type === 'event' || type === 'legal' || withAppointment,
      then: yup.object().shape({
        allDay: yup.bool().required(),
        startDate: yup.date().required(t(translations.validation.required())),
        endDate: yup.date().when(
          'startDate',
          startDate =>
            startDate &&
            yup
              .date()
              .min(
                startDate,
                t(translations.validation.minDate(), {
                  date: new Date(startDate).toLocaleDateString()
                })
              )
              .nullable()
        ),
        duration: yup.number().when('allDay', {
          is: false,
          then: yup.number().required(t(translations.validation.required()))
        }),
        venue: yup.string().notRequired()
      })
    })
    .when('type', {
      is: type => type === 'holiday',
      then: yup.object().shape({
        allDay: yup.bool().equals([true]).required(t(translations.validation.required())),
        startDate: yup.date().required(t(translations.validation.required())),
        endDate: yup.date().when(
          'startDate',
          startDate =>
            startDate &&
            yup
              .date()
              .min(
                startDate,
                t(translations.validation.minDate(), {
                  date: new Date(startDate).toLocaleDateString()
                })
              )
              .nullable()
        ),
        duration: yup.number().when('allDay', {
          is: false,
          then: yup.number().required(t(translations.validation.required()))
        }),
        venue: yup.string().notRequired()
      })
    }),
  startDate: yup.date().required(t(translations.validation.required())),
  organizers: yup.array().of(
    yup.object().shape({
      refId: yup.number().required(),
      organizerType: yup.string().oneOf(Object.values(OrganizerType)).required()
    })
  ),
  endDate: yup.date().when(
    'startDate',
    startDate =>
      startDate &&
      yup
        .date()
        .min(
          startDate,
          t(translations.validation.minDate(), {
            date: new Date(startDate).toLocaleDateString()
          })
        )
        .notRequired()
        .nullable()
  ),
  attachement: yup.mixed()
});

export const articleInitialValues = (article: Article | undefined, isSuperUser: boolean) => {
  return article
    ? {
        ...pick(article, [
          'id',
          'headline',
          'shortDescription',
          'type',
          'articleText',
          'startDate',
          'endDate',
          'recipients',
          'organizers',
          'semesters',
          'isPushNotificationEnabled',
          'isSendMailDisabled'
        ]),
        withAppointment:
          !!article.appointment?.startDate ||
          article.type === 'event' ||
          article.type === 'legal' ||
          article.type === 'holiday',
        appointment: article.appointment
          ? {
              ...omit(article.appointment, ['id', '__typename']),
              duration:
                isSameDay(article.appointment.startDate, article.appointment.endDate) &&
                !article.appointment.allDay
                  ? differenceInMinutes(article.appointment.endDate, article.appointment.startDate)
                  : 30,

              allDay: article.appointment.allDay ? article.appointment.allDay : false
            }
          : {
              allDay: false,
              startDate: set(addDays(new Date(), 1), {
                hours: 8,
                minutes: 0,
                seconds: 0,
                milliseconds: 0
              }),
              endDate: null,
              duration: 30,
              venue: ''
            },
        author: article.author ? article.author : {},
        postAs: article.author?.postAs ? article.author.postAs : '',
        department: article.department ? article.department.id.toString() : '',
        isSuperUser
      }
    : {
        author: {},
        postAs: '',
        recipients: [],
        semesters: [],
        department: '',
        headline: '',
        shortDescription: '',
        type: 'general',
        articleText: '',
        withAppointment: false,
        appointment: {
          allDay: false,
          startDate: set(addDays(new Date(), 1), {
            hours: 8,
            minutes: 0,
            seconds: 0,
            milliseconds: 0
          }),
          endDate: null,
          duration: 30,
          venue: ''
        },
        startDate: new Date(),
        endDate: null,
        isSuperUser,
        organizers: [],
        isPushNotificationEnabled: false,
        isSendMailDisabled: false
      };
};
